import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Owns the Download button's state and behavior:
 *   - `hasDownloaded` persists across remounts via localStorage so the
 *     button hides on revisit after a successful download.
 *   - `handleDownload` runs `/finalize/` first if the job hasn't been
 *     finalized yet (so whatever answers exist — manual PATCHes or prior
 *     `/fill/` outputs — get baked in), then GETs the blob and triggers
 *     a browser download. RAG `/fill/` is intentionally NOT called here;
 *     that's opt-in via the Generate Answer button on the page.
 */
export default function useDownloadJob({
  jobId,
  job,
  isFinalizedView,
  applyJobData,
  setReadyToFinalize,
  setFillStage,
  manualMode = false,
}) {
  const [downloading, setDownloading] = useState(false);
  const downloadedKey = `formReview:downloaded:${jobId}`;
  const [hasDownloaded, setHasDownloaded] = useState(() => {
    try {
      return localStorage.getItem(downloadedKey) === "1";
    } catch {
      return false;
    }
  });

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Manual mode re-bakes on every download so the latest edits (added
      // text, resizes, moves) are reflected — and stays editable afterwards.
      // AI mode finalizes once, then serves the baked blob.
      if (manualMode || !isFinalizedView) {
        setFillStage("finalizing");
        const finalizeRes = await api.post(
          `/api/v1/rfp/autopdf/jobs/${jobId}/finalize/`,
          {},
          { timeout: 0 }
        );
        if (![200, 201].includes(finalizeRes.status)) {
          throw new Error("Finalize request failed");
        }
        const finalized = finalizeRes.data;
        if (finalized?.error_message) {
          throw new Error(finalized.error_message);
        }
        if (finalized?.status !== "completed") {
          throw new Error(
            `Finalize returned status "${finalized?.status || "unknown"}", expected "completed"`
          );
        }
        // In manual mode, don't swap the viewer to the baked PDF — keep the
        // editable overlays on the source so the user can keep editing.
        if (!manualMode) {
          const merged = {
            ...(job || {}),
            ...finalized,
            fields: finalized?.fields ?? job?.fields ?? [],
          };
          applyJobData(merged);
          setReadyToFinalize(false);
        }
        setFillStage("idle");
      }

      const res = await api.get(
        `/api/v1/rfp/autopdf/jobs/${jobId}/download/`,
        { responseType: "blob" }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = job?.name || `filled_form_${jobId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      // Manual mode stays editable after download, so don't hide the toolbar.
      if (!manualMode) {
        setHasDownloaded(true);
        try {
          localStorage.setItem(downloadedKey, "1");
        } catch {
          /* ignore quota/availability errors */
        }
      }
      toast.success("Downloaded.");
    } catch (err) {
      console.error("Download failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to download PDF.";
      toast.error(msg);
    } finally {
      setDownloading(false);
      // Reset finalize stage in case the auto-finalize threw before
      // we got to reset it ourselves.
      setFillStage("idle");
    }
  }, [
    downloading,
    isFinalizedView,
    jobId,
    job,
    applyJobData,
    setReadyToFinalize,
    setFillStage,
    downloadedKey,
    manualMode,
  ]);

  const clearDownloaded = useCallback(() => {
    setHasDownloaded(false);
    try {
      localStorage.removeItem(downloadedKey);
    } catch {
      /* ignore */
    }
  }, [downloadedKey]);

  return {
    downloading,
    hasDownloaded,
    handleDownload,
    clearDownloaded,
  };
}
